import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Warning, ClockCounterClockwise, Wrench, ClipboardText, Siren, ArrowsClockwise } from "@phosphor-icons/react";
import API from "../lib/api";

const KIND = {
  request: { label: "Request", icon: ClipboardText, tint: "text-sky-600 dark:text-sky-400", path: (p, id) => (p ? `/portal/requests/${id}` : `/job-requests/${id}`) },
  job: { label: "Job", icon: Wrench, tint: "text-amber-600 dark:text-amber-400", path: (p, id) => (p ? `/portal/jobs/${id}` : `/jobs/${id}`) },
  visit: { label: "Contract visit", icon: ArrowsClockwise, tint: "text-teal-600 dark:text-teal-400", path: (p, id) => (p ? `/portal/jobs/${id}` : `/jobs/${id}`) },
  emergency: { label: "Emergency", icon: Siren, tint: "text-red-600 dark:text-red-400", path: (p, id) => (p ? `/portal/emergencies/${id}` : `/emergency-jobs/${id}`) },
};
const STATUS_TINT = (s) => (["completed", "resolved", "converted", "closed", "client_approved"].includes(s) ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
  : ["cancelled", "declined"].includes(s) ? "bg-slate-100 text-slate-500 dark:bg-slate-800"
  : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300");
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const nice = (s) => (s || "").replace(/_/g, " ");

/**
 * Timeline of every request / job / contract visit / emergency at a property, with repeat-fault badges.
 * endpoint: admin `/clients/{cid}/properties/{pid}/history` or portal `/client-hub/properties/{pid}/history`.
 */
export default function PropertyHistory({ endpoint, portal = false, limit = 12, testid = "property-history" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setData(null);
    API.get(endpoint).then(({ data: d }) => setData(d)).catch(() => setError(true));
  }, [endpoint]);

  if (error) return <p className="text-xs text-muted-foreground italic" data-testid={`${testid}-error`}>History unavailable.</p>;
  if (!data) return <p className="text-xs text-muted-foreground italic" data-testid={`${testid}-loading`}>Loading history…</p>;

  const { summary, repeat_faults: repeats, items } = data;
  const shown = showAll ? items : items.slice(0, limit);

  return (
    <div className="space-y-2.5" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-1.5 text-[10px]" data-testid={`${testid}-summary`}>
        <span className="inline-flex items-center gap-1 font-semibold text-muted-foreground"><ClockCounterClockwise size={12} /> {summary.total} on record</span>
        <span className="px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300">{summary.requests} requests</span>
        <span className="px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">{summary.jobs} jobs</span>
        <span className="px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">{summary.visits} contract visits</span>
        <span className="px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300">{summary.emergencies} emergencies</span>
      </div>

      {repeats.length > 0 && (
        <div className="rounded-md border border-red-200 dark:border-red-900/60 bg-red-50/70 dark:bg-red-950/30 px-2.5 py-2" data-testid={`${testid}-repeats`}>
          <p className="text-[10px] font-semibold text-red-800 dark:text-red-300 inline-flex items-center gap-1"><Warning size={12} weight="fill" /> Repeat faults at this property</p>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {repeats.map(r => (
              <span key={r.category} className="inline-flex items-center gap-1 rounded-full bg-white dark:bg-slate-900 border border-red-200 dark:border-red-800 px-2 py-0.5 text-[11px]" title={`Last: ${fmt(r.last)} · ${r.kinds.join(", ")}`} data-testid={`${testid}-repeat-${r.category}`}>
                <span className="font-semibold capitalize">{nice(r.category)}</span>
                <span className="text-red-700 dark:text-red-300 font-bold">×{r.count}</span>
                <span className="text-muted-foreground">last {fmt(r.last)}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground italic" data-testid={`${testid}-empty`}>Nothing logged at this property yet.</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border bg-card" data-testid={`${testid}-list`}>
          {shown.map(it => {
            const k = KIND[it.kind] || KIND.job;
            const Icon = k.icon;
            return (
              <li key={`${it.kind}-${it.id}`}>
                <Link to={k.path(portal, it.id)} className="flex items-center gap-2.5 px-2.5 py-1.5 hover:bg-muted/50 transition-colors" data-testid={`${testid}-item-${it.id}`}>
                  <Icon size={14} className={`${k.tint} shrink-0`} />
                  <span className="text-[10px] text-muted-foreground w-20 shrink-0">{fmt(it.date)}</span>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground w-24 shrink-0 hidden sm:inline">{k.label}</span>
                  <span className="text-xs flex-1 truncate"><span className="font-mono text-[10px] text-muted-foreground mr-1">{it.reference}</span>{it.title}{it.category ? <span className="text-muted-foreground"> · {nice(it.category)}</span> : null}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full capitalize shrink-0 ${STATUS_TINT(it.status)}`}>{nice(it.status)}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
      {items.length > limit && (
        <button type="button" onClick={() => setShowAll(v => !v)} className="text-[11px] text-primary hover:underline" data-testid={`${testid}-toggle-all`}>
          {showAll ? "Show fewer" : `Show all ${items.length}`}
        </button>
      )}
    </div>
  );
}
